import { useState } from "react";
import { BookOpen } from "lucide-react";

import { cn } from "@/lib/utils";

import type { GuideTarget, ShiftRole } from "../lib";
import { useShiftGuides } from "../useShiftGuides";
import { DeptPill } from "./ShiftGuideCard";
import { ShiftGuideDialog } from "./ShiftGuideDialog";
import { ShiftGuideStatus } from "./ShiftGuideStatus";

/**
 * The guides for one shift as a short list, for the shift detail on the
 * schedule (decision 0128). Each row opens the guide dialog on that guide.
 */
export function ShiftGuideBlock({
  target,
  role,
  subtitle,
  className,
}: {
  target: GuideTarget;
  role: ShiftRole;
  /** Dialog sub-heading naming the shift: day · time · package · role. */
  subtitle: string;
  className?: string | undefined;
}) {
  const guides = useShiftGuides(target, role);
  const [openId, setOpenId] = useState<string | null>(null);
  const list = guides.groups.flatMap((group) => group.guides);

  return (
    <div className={cn("rounded-md border bg-surface px-3 pb-2.5 pt-3", className)}>
      <div className="flex flex-wrap items-center gap-x-1.5 text-[13.5px] font-bold">
        <BookOpen size={16} aria-hidden className="shrink-0 text-[var(--color-brand)]" />
        <span>Hướng dẫn cho ca này</span>
        {guides.summary ? (
          <span className="text-[12.5px] font-medium text-muted-foreground">· {guides.summary}</span>
        ) : null}
      </div>

      {guides.status === "ready" && list.length > 0 ? (
        <ul className="mt-2 space-y-1.5">
          {list.map((guide) => (
            <li key={guide.id}>
              <button
                type="button"
                aria-label={`Mở bài: ${guide.title}`}
                onClick={() => setOpenId(guide.id)}
                className="flex min-h-11 w-full items-center gap-2 rounded-md bg-surface-2 px-2.5 py-2 text-left text-[12.5px]"
              >
                <span className="min-w-0 flex-1 font-semibold leading-snug">{guide.title}</span>
                <DeptPill dept={guide.dept} />
                <span className="shrink-0 font-bold text-[var(--color-brand)]">Mở ›</span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <ShiftGuideStatus
          status={guides.status === "ready" ? "empty" : guides.status}
          onRetry={guides.retry}
          className="mt-2"
        />
      )}

      <ShiftGuideDialog
        open={openId !== null}
        onOpenChange={(next) => {
          if (!next) setOpenId(null);
        }}
        subtitle={subtitle}
        role={role}
        groups={guides.groups}
        groupTitle={guides.groupTitle}
        openGuideId={openId ?? undefined}
      />
    </div>
  );
}
